<template>
  <div
    class="relative w-full overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-2xl transition-all duration-500 dark:border-white/10 dark:bg-[#09090b] sm:rounded-[32px]"
  >
    <header
      class="relative z-20 flex h-16 items-center justify-between border-b border-zinc-200 bg-white/80 px-4 backdrop-blur-md transition-colors duration-300 dark:border-white/5 dark:bg-[#0c0c0e]/80 sm:px-6"
    >
      <div class="flex items-center gap-3">
        <div
          class="group flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-900 text-white transition-transform duration-500 hover:rotate-180 dark:bg-white dark:text-black"
        >
          <span class="material-symbols-rounded text-lg">contrast</span>
        </div>
        <div>
          <h2 class="text-sm font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
            Contrast Studio
          </h2>
          <p class="hidden text-[10px] text-zinc-500 sm:block">WCAG 2.1 Compliance Lab</p>
        </div>
      </div>
      
      <div class="flex items-center gap-3">
  
  <router-link to="/dashboard/hub"
    class="group flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-500 transition-all duration-300 hover:-translate-y-0.5 hover:border-zinc-300 hover:text-zinc-900 hover:shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white"
    title="Tutorial Hub">
    <span class="material-symbols-rounded text-lg transition-transform duration-300 group-hover:rotate-12">school</span>
  </router-link>

  <div class="hidden items-center gap-2 rounded-full border border-zinc-200 bg-zinc-100 px-3 py-1.5 transition-colors dark:border-white/5 dark:bg-white/5 sm:flex">
    <div class="h-2 w-2 animate-pulse rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
    <span class="text-[10px] font-medium text-zinc-600 dark:text-zinc-400">Live Analysis</span>
  </div>

</div>
    </header>

    <div class="flex flex-col overflow-hidden lg:grid lg:h-[800px] lg:grid-cols-[340px_1fr]">
      
      <aside class="relative z-10 flex flex-col gap-6 border-b border-zinc-200 bg-white p-4 transition-colors duration-300 dark:border-white/5 dark:bg-[#0c0c0e] lg:border-b-0 lg:border-r lg:p-6 lg:overflow-y-auto">
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Project Palette</span>
            <span class="text-[10px] text-zinc-400">{{ paletteColors.length }} swatches</span>
          </div>
          
          <div class="grid grid-cols-5 gap-2 sm:grid-cols-6">
            <button
              v-for="(c, i) in paletteColors"
              :key="c.id"
              class="animate-enter-scale group relative aspect-square w-full overflow-hidden rounded-full border border-black/5 ring-offset-2 transition-all duration-300 hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 dark:border-white/10 dark:ring-offset-zinc-950"
              :class="activeRingClass(c.hex)"
              :style="{ backgroundColor: c.hex, animationDelay: `${i * 30}ms` }"
              :title="c.name"
              @click="setFg(c.hex)"
            >
             <transition name="scale-fade">
               <div v-if="c.hex === fgHex" class="absolute inset-0 flex items-center justify-center">
                  <span class="h-1.5 w-1.5 rounded-full bg-white shadow-sm ring-1 ring-black/20"></span>
               </div>
             </transition>
             <transition name="scale-fade">
               <div v-if="c.hex === bgHex" class="absolute inset-0 flex items-center justify-center">
                  <span class="h-3 w-3 rounded-full border-[1.5px] border-white/80 shadow-sm"></span>
               </div>
             </transition>
            </button>
          </div>
        </div>

        <hr class="border-zinc-100 transition-colors dark:border-white/5" />

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-bold uppercase tracking-wider text-zinc-900 dark:text-zinc-200">Foreground</span>
                <span class="hidden rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-500 transition-colors dark:bg-white/5 dark:text-zinc-400 sm:inline-block">Text</span>
              </div>
              <div class="group relative flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 transition-all duration-300 hover:border-zinc-300 hover:shadow-md dark:border-white/5 dark:bg-white/[0.02] dark:hover:border-white/10">
                <input type="color" v-model="fgHexRef" class="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
                <div class="h-10 w-10 shrink-0 rounded-lg border border-black/5 shadow-sm transition-transform duration-300 group-hover:scale-105 dark:border-white/10" :style="{ backgroundColor: fgHex }"></div>
                <div class="flex flex-1 flex-col justify-center overflow-hidden">
                  <span class="font-mono text-xs font-semibold uppercase text-zinc-900 transition-colors dark:text-zinc-100">{{ fgHex }}</span>
                  <span class="truncate text-[10px] text-zinc-500">{{ findNameForHex(fgHex) || 'Custom' }}</span>
                </div>
                <div class="grid h-6 w-6 place-items-center rounded bg-white text-zinc-400 shadow-sm transition-transform duration-300 group-hover:rotate-12 dark:bg-white/10">
                  <span class="material-symbols-rounded text-xs">colorize</span>
                </div>
              </div>
            </div>

            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-bold uppercase tracking-wider text-zinc-900 dark:text-zinc-200">Background</span>
                <span class="hidden rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-500 transition-colors dark:bg-white/5 dark:text-zinc-400 sm:inline-block">Surface</span>
              </div>
              <div class="group relative flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 transition-all duration-300 hover:border-zinc-300 hover:shadow-md dark:border-white/5 dark:bg-white/[0.02] dark:hover:border-white/10">
                <input type="color" v-model="bgHexRef" class="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
                <div class="h-10 w-10 shrink-0 rounded-lg border border-black/5 shadow-sm transition-transform duration-300 group-hover:scale-105 dark:border-white/10" :style="{ backgroundColor: bgHex }"></div>
                <div class="flex flex-1 flex-col justify-center overflow-hidden">
                  <span class="font-mono text-xs font-semibold uppercase text-zinc-900 transition-colors dark:text-zinc-100">{{ bgHex }}</span>
                  <span class="truncate text-[10px] text-zinc-500">{{ findNameForHex(bgHex) || 'Custom' }}</span>
                </div>
                <div class="grid h-6 w-6 place-items-center rounded bg-white text-zinc-400 shadow-sm transition-transform duration-300 group-hover:rotate-12 dark:bg-white/10">
                  <span class="material-symbols-rounded text-xs">colorize</span>
                </div>
              </div>
            </div>
        </div>

        <div class="mt-auto flex gap-2 pt-2 lg:pt-0">
           <button @click="swapColors" class="flex-1 transform rounded-lg border border-zinc-200 bg-white py-2.5 text-[10px] font-semibold text-zinc-600 transition-all duration-200 hover:-translate-y-0.5 hover:bg-zinc-50 hover:text-zinc-900 hover:shadow-md active:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white">
             Swap
           </button>
           <button @click="snapToBestContrast" class="flex-1 transform rounded-lg bg-zinc-900 py-2.5 text-[10px] font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-zinc-800 hover:shadow-lg active:scale-95 dark:bg-white dark:text-black dark:hover:bg-zinc-200">
             Auto Fix
           </button>
        </div>
      </aside>

      <main class="flex flex-col bg-zinc-50/50 p-4 transition-colors duration-500 dark:bg-[#050505] sm:p-6 lg:overflow-y-auto">
        
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6">
            <section class="relative flex flex-col justify-between overflow-hidden rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm transition-all duration-300 hover:shadow-md dark:border-white/5 dark:bg-[#0c0c0e] lg:p-6">
                <div class="relative z-10">
                    <h3 class="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Contrast Score</h3>
                    <div class="mt-2 flex items-baseline gap-1">
                        <span class="animate-number-pop text-5xl font-black tracking-tighter text-zinc-900 transition-colors duration-300 dark:text-white sm:text-6xl">{{ animatedRatio }}</span>
                        <span class="text-xl font-medium text-zinc-400">:1</span>
                    </div>
                </div>
                <div class="relative z-10 mt-6 flex items-center gap-3">
                    <div class="flex items-center gap-2 rounded-full border border-black/5 bg-zinc-50 px-3 py-1.5 transition-all duration-300 dark:border-white/5 dark:bg-white/5">
                        <div class="h-2 w-2 rounded-full transition-colors duration-300" :class="statusColorClass"></div>
                        <span class="text-xs font-bold transition-colors duration-300" :class="statusTextClass">{{ ratingLabel }}</span>
                    </div>
                </div>
                <div class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-20 blur-3xl transition-colors duration-700" :style="{ backgroundColor: fgHex }"></div>
                <div class="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full opacity-10 blur-3xl transition-colors duration-700" :style="{ backgroundColor: bgHex === '#FFFFFF' ? '#000' : bgHex }"></div>
            </section>

            <section class="grid grid-cols-3 gap-3">
                <div v-for="(grade, label, idx) in { Normal: normalRating, Large: largeRating, UI: uiRating }" :key="label" 
                     class="animate-enter-up flex flex-col justify-between rounded-2xl border border-zinc-200 bg-white p-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/5 dark:bg-[#0c0c0e] lg:p-4"
                     :style="{ animationDelay: `${idx * 100}ms` }">
                    <span class="text-[9px] font-bold uppercase tracking-wider text-zinc-400 sm:text-[10px]">{{ label }}</span>
                    <div class="mt-2 flex items-center justify-between gap-1">
                         <div class="text-xl font-black tracking-tight transition-colors duration-300 sm:text-2xl" :class="textGradeClass(grade)">{{ grade }}</div>
                         <transition name="scale-rotate" mode="out-in">
                           <div v-if="grade === 'AAA'" class="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white shadow-sm sm:h-5 sm:w-5">
                               <span class="material-symbols-rounded text-[10px] font-bold">check</span>
                           </div>
                           <div v-else-if="grade === 'Fail'" class="grid h-4 w-4 place-items-center rounded-full bg-rose-500 text-white shadow-sm sm:h-5 sm:w-5">
                               <span class="material-symbols-rounded text-[10px] font-bold">close</span>
                           </div>
                         </transition>
                    </div>
                </div>
            </section>
        </div>

        <h4 class="mb-3 mt-6 text-[11px] font-bold uppercase tracking-widest text-zinc-400 lg:mt-8">UI Simulation</h4>
        
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
            <div class="overflow-hidden rounded-2xl border border-zinc-200 shadow-sm transition-all duration-500 ease-out dark:border-white/5" :style="{ backgroundColor: bgHex }">
                <div class="border-b border-black/5 px-4 py-3 transition-colors duration-500 dark:border-white/5">
                    <div class="flex items-center justify-between">
                        <div class="text-[10px] font-bold uppercase tracking-widest opacity-50 transition-colors duration-500" :style="{ color: fgHex }">App View</div>
                        <div class="flex gap-1.5">
                            <div class="h-2 w-2 rounded-full opacity-30 transition-colors duration-500" :style="{ backgroundColor: fgHex }"></div>
                            <div class="h-2 w-2 rounded-full opacity-30 transition-colors duration-500" :style="{ backgroundColor: fgHex }"></div>
                        </div>
                    </div>
                </div>
                <div class="p-5">
                    <div class="mb-1 text-xs font-medium opacity-60 transition-colors duration-500" :style="{ color: fgHex }">Dashboard</div>
                    <div class="mb-4 text-2xl font-bold tracking-tight transition-colors duration-500" :style="{ color: fgHex }">Daily Overview</div>
                    
                    <div class="flex flex-col gap-3">
                        <div class="rounded-xl border border-black/10 p-4 transition-all duration-500 dark:border-white/10" :style="{ borderColor: fgHex + '20' }">
                            <div class="mb-2 flex items-center gap-2">
                                <div class="h-8 w-8 rounded-lg opacity-20 transition-colors duration-500" :style="{ backgroundColor: fgHex }"></div>
                                <div class="text-sm font-bold transition-colors duration-500" :style="{ color: fgHex }">Activity Log</div>
                            </div>
                            <p class="text-xs leading-relaxed opacity-70 transition-colors duration-500" :style="{ color: fgHex }">
                                Your team created 12 new projects.
                            </p>
                        </div>
                        <button class="group w-full rounded-lg py-2.5 text-xs font-bold uppercase tracking-wide transition-all duration-300 active:scale-95" 
                                :style="{ backgroundColor: fgHex, color: bgHex }">
                            <span class="inline-block transition-transform duration-300 group-hover:scale-105">Primary Action</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-4">
                 <div class="rounded-2xl border border-zinc-200 p-5 transition-all duration-500 ease-out dark:border-white/5" :style="{ backgroundColor: bgHex }">
                     <p class="mb-3 text-[10px] uppercase tracking-widest opacity-40 transition-colors duration-500" :style="{ color: fgHex }">Typography Scale</p>
                     <div class="space-y-4">
                         <div class="group">
                             <p class="text-xs opacity-50 transition-colors duration-500" :style="{ color: fgHex }">Small (12px)</p>
                             <p class="text-xs transition-colors duration-500" :style="{ color: fgHex }">The quick brown fox jumps.</p>
                         </div>
                         <div class="group">
                             <p class="text-xs opacity-50 transition-colors duration-500" :style="{ color: fgHex }">Regular (16px)</p>
                             <p class="text-base transition-colors duration-500" :style="{ color: fgHex }">The quick brown fox jumps.</p>
                         </div>
                         <div class="group">
                             <p class="text-xs opacity-50 transition-colors duration-500" :style="{ color: fgHex }">Heading (24px)</p>
                             <p class="text-xl font-bold transition-colors duration-500 sm:text-2xl" :style="{ color: fgHex }">The quick brown fox.</p>
                         </div>
                     </div>
                 </div>

                 <div class="grid grid-cols-2 gap-4">
                     <div class="flex items-center justify-center rounded-2xl border border-zinc-200 p-4 transition-all duration-500 ease-out dark:border-white/5" :style="{ backgroundColor: bgHex }">
                         <div class="flex items-center gap-2 text-sm font-medium transition-colors duration-500" :style="{ color: fgHex }">
                             <span class="material-symbols-rounded animate-bounce-subtle">check_circle</span>
                             <span class="hidden sm:inline">Icon</span>
                         </div>
                     </div>
                     <div class="flex items-center justify-center rounded-2xl border border-zinc-200 p-4 transition-all duration-500 ease-out dark:border-white/5" :style="{ backgroundColor: bgHex }">
                         <input type="text" value="Input" readonly class="w-full border-b bg-transparent py-1 text-sm outline-none transition-colors duration-500" :style="{ borderColor: fgHex + '60', color: fgHex }" />
                     </div>
                 </div>
            </div>
        </div>

        <h4 class="mb-3 mt-6 text-[11px] font-bold uppercase tracking-widest text-zinc-400 lg:mt-8">Palette Matrix</h4>
        
        <div class="flex max-h-[400px] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition-all duration-300 dark:border-white/5 dark:bg-[#0c0c0e]">
             <div class="custom-scrollbar overflow-auto">
                 <table class="w-full min-w-[500px] text-left text-[11px]">
                     <thead class="sticky top-0 z-10 border-b border-zinc-100 bg-zinc-50/95 backdrop-blur dark:border-white/5 dark:bg-[#18181b]/95">
                         <tr>
                             <th class="px-4 py-3 font-semibold text-zinc-500">Foreground</th>
                             <th class="px-4 py-3 font-semibold text-zinc-500">Background</th>
                             <th class="px-4 py-3 font-semibold text-zinc-500">Ratio</th>
                             <th class="px-4 py-3 text-right font-semibold text-zinc-500">Grade</th>
                         </tr>
                     </thead>
                     <tbody class="divide-y divide-zinc-100 dark:divide-white/5">
                         <tr v-for="(pair, idx) in samplePairs" :key="pair.fg + pair.bg" 
                             class="group cursor-pointer transition-colors duration-200 hover:bg-zinc-50 dark:hover:bg-white/[0.04]"
                             :class="{ 'animate-enter-up': idx < 10 }"
                             :style="{ animationDelay: `${idx * 50}ms` }"
                             @click="applyPair(pair)">
                             <td class="px-4 py-2.5">
                                 <div class="flex items-center gap-2">
                                     <div class="h-3 w-3 rounded-full border border-black/10 transition-transform duration-200 group-hover:scale-125 dark:border-white/10" :style="{ backgroundColor: pair.fg }"></div>
                                     <span class="font-mono text-zinc-600 transition-colors group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-200">{{ pair.fg }}</span>
                                 </div>
                             </td>
                             <td class="px-4 py-2.5">
                                 <div class="flex items-center gap-2">
                                     <div class="h-3 w-3 rounded-full border border-black/10 transition-transform duration-200 group-hover:scale-125 dark:border-white/10" :style="{ backgroundColor: pair.bg }"></div>
                                     <span class="font-mono text-zinc-600 transition-colors group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-200">{{ pair.bg }}</span>
                                 </div>
                             </td>
                             <td class="px-4 py-2.5 font-bold text-zinc-700 dark:text-zinc-300">
                                 {{ pair.ratio.toFixed(2) }}
                             </td>
                             <td class="px-4 py-2.5 text-right">
                                 <div class="flex justify-end gap-1">
                                     <span v-if="pair.normal === 'AAA'" class="rounded bg-emerald-100 px-1 text-[9px] font-bold text-emerald-700 transition-transform duration-300 group-hover:scale-110 dark:bg-emerald-500/20 dark:text-emerald-400">AAA</span>
                                     <span v-else-if="pair.normal === 'AA'" class="rounded bg-sky-100 px-1 text-[9px] font-bold text-sky-700 transition-transform duration-300 group-hover:scale-110 dark:bg-sky-500/20 dark:text-sky-400">AA</span>
                                     <span v-else class="rounded bg-zinc-100 px-1 text-[9px] font-bold text-zinc-400 transition-opacity group-hover:opacity-50 dark:bg-white/10">-</span>
                                 </div>
                             </td>
                         </tr>
                     </tbody>
                 </table>
             </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'

const props = defineProps({
  palette: {
    type: Array,
    default: () => [
      { hex: '#0F172A', name: 'Midnight Slate' },
      { hex: '#3b82f6', name: 'Blue 500' },
      { hex: '#22C55E', name: 'Success' },
      { hex: '#F97316', name: 'Warning' },
      { hex: '#EF4444', name: 'Danger' },
      { hex: '#F8FAFC', name: 'Slate 50' },
      { hex: '#64748B', name: 'Slate 500' },
      { hex: '#1e293b', name: 'Slate 800' }
    ]
  }
})

const animatedRatio = ref(0)
const paletteColors = ref([])
const fgHexRef = ref('#000000')
const bgHexRef = ref('#FFFFFF')

onMounted(() => {
    paletteColors.value = (props.palette || []).map((c, index) => ({
      id: index,
      hex: normalizeHex(c.hex),
      name: c.name || ''
    })).filter(c => c.hex)

    fgHexRef.value = paletteColors.value[0]?.hex || '#000000'
    bgHexRef.value = paletteColors.value[5]?.hex || '#FFFFFF'
})

function normalizeHex(value) {
  if (!value) return ''
  let v = String(value).trim()
  if (!v.startsWith('#')) v = `#${v}`
  v = v.slice(0, 7)
  return v.toUpperCase()
}

function hexToRgb(hex) {
  const v = normalizeHex(hex)
  if (v.length !== 7) return { r: 0, g: 0, b: 0 }
  const r = parseInt(v.slice(1, 3), 16)
  const g = parseInt(v.slice(3, 5), 16)
  const b = parseInt(v.slice(5, 7), 16)
  return { r, g, b }
}

function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex)
  const rsrgb = r / 255
  const gsrgb = g / 255
  const bsrgb = b / 255
  const rLin = rsrgb <= 0.03928 ? rsrgb / 12.92 : Math.pow((rsrgb + 0.055) / 1.055, 2.4)
  const gLin = gsrgb <= 0.03928 ? gsrgb / 12.92 : Math.pow((gsrgb + 0.055) / 1.055, 2.4)
  const bLin = bsrgb <= 0.03928 ? bsrgb / 12.92 : Math.pow((bsrgb + 0.055) / 1.055, 2.4)
  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin
}

function contrastBetween(fg, bg) {
  const L1 = relativeLuminance(fg)
  const L2 = relativeLuminance(bg)
  const max = Math.max(L1, L2)
  const min = Math.min(L1, L2)
  return (max + 0.05) / (min + 0.05)
}

function ratingForRatio(ratio, type) {
  if (type === 'normal') {
    if (ratio >= 7) return 'AAA'
    if (ratio >= 4.5) return 'AA'
    if (ratio >= 3) return 'A'
    return 'Fail'
  }
  if (type === 'large' || type === 'ui') {
    if (ratio >= 4.5) return 'AAA'
    if (ratio >= 3) return 'AA'
    return 'Fail'
  }
  return 'Fail'
}

const fgHex = computed(() => normalizeHex(fgHexRef.value))
const bgHex = computed(() => normalizeHex(bgHexRef.value))

const contrastRatio = computed(() => contrastBetween(fgHex.value, bgHex.value))

watch(contrastRatio, (newVal) => {
    animateValue(animatedRatio.value, newVal, 600)
}, { immediate: true })

function animateValue(start, end, duration) {
    let startTimestamp = null;
    const startNum = parseFloat(start);
    const endNum = parseFloat(end);

    const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        
        const currentVal = startNum + (endNum - startNum) * easeOutQuart;
                animatedRatio.value = currentVal.toFixed(2);

        if (progress < 1) {
            window.requestAnimationFrame(step);
        }
    };
    window.requestAnimationFrame(step);
}

const normalRating = computed(() => ratingForRatio(contrastRatio.value, 'normal'))
const largeRating = computed(() => ratingForRatio(contrastRatio.value, 'large'))
const uiRating = computed(() => ratingForRatio(contrastRatio.value, 'ui'))

const normalizedOverallRating = computed(() => {
  if (normalRating.value === 'AAA') return 'AAA'
  if (normalRating.value === 'AA') return 'AA'
  if (largeRating.value === 'AA') return 'AA Large'
  return 'Fail'
})

const ratingLabel = computed(() => {
  if (normalizedOverallRating.value === 'AAA') return 'Excellent'
  if (normalizedOverallRating.value === 'AA') return 'Good'
  if (normalizedOverallRating.value === 'AA Large') return 'Large Text'
  return 'Poor'
})

const statusColorClass = computed(() => {
  const r = normalizedOverallRating.value
  if (r === 'AAA') return 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]'
  if (r === 'AA') return 'bg-sky-500 shadow-[0_0_10px_rgba(14,165,233,0.5)]'
  if (r === 'AA Large') return 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]'
  return 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]'
})

const statusTextClass = computed(() => {
  const r = normalizedOverallRating.value
  if (r === 'AAA') return 'text-emerald-600 dark:text-emerald-400'
  if (r === 'AA') return 'text-sky-600 dark:text-sky-400'
  if (r === 'AA Large') return 'text-amber-600 dark:text-amber-400'
  return 'text-rose-600 dark:text-rose-400'
})

function textGradeClass(grade) {
    if (grade === 'AAA') return 'text-emerald-500'
    if (grade === 'AA') return 'text-sky-500'
    if (grade === 'Fail') return 'text-rose-500 opacity-50'
    return 'text-zinc-500'
}

function activeRingClass(hex) {
    if (hex === fgHex.value) return 'ring-2 ring-zinc-900 dark:ring-white scale-110 z-10 shadow-lg'
    if (hex === bgHex.value) return 'ring-2 ring-zinc-400 dark:ring-zinc-600 scale-110 z-10 shadow-lg'
    return 'hover:border-zinc-400 dark:hover:border-zinc-500'
}

const samplePairs = computed(() => {
  const list = []
  const colors = paletteColors.value
  for (let i = 0; i < colors.length; i++) {
    for (let j = 0; j < colors.length; j++) {
      if (i === j) continue
      const fg = colors[i].hex
      const bg = colors[j].hex
      const r = contrastBetween(fg, bg)
      list.push({ 
          fg, bg, ratio: r, 
          normal: ratingForRatio(r, 'normal')
      })
    }
  }
  return list.sort((a, b) => b.ratio - a.ratio)
})

function findNameForHex(hex) {
  return paletteColors.value.find(c => c.hex === normalizeHex(hex))?.name || ''
}

function swapColors() {
  const temp = fgHexRef.value
  fgHexRef.value = bgHexRef.value
  bgHexRef.value = temp
}

function setFg(hex) {
    fgHexRef.value = hex
}

function snapToBestContrast() {
    let bestRatio = 0
    let bestBg = bgHex.value
    
    paletteColors.value.forEach(c => {
        const r = contrastBetween(fgHex.value, c.hex)
        if (r > bestRatio) {
            bestRatio = r
            bestBg = c.hex
        }
    })
    bgHexRef.value = bestBg
}

function applyPair(pair) {
    fgHexRef.value = pair.fg
    bgHexRef.value = pair.bg
}
</script>

<style>
@keyframes enter-scale {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes enter-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes bounce-subtle {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-2px); }
}

.animate-enter-scale {
  animation: enter-scale 0.4s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.animate-enter-up {
  animation: enter-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.animate-bounce-subtle {
    animation: bounce-subtle 2s infinite ease-in-out;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.2s ease;
}

.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

.scale-rotate-enter-active,
.scale-rotate-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.scale-rotate-enter-from,
.scale-rotate-leave-to {
  opacity: 0;
  transform: scale(0) rotate(-45deg);
}

aside::-webkit-scrollbar,
main::-webkit-scrollbar,
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
aside::-webkit-scrollbar-track,
main::-webkit-scrollbar-track,
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
aside::-webkit-scrollbar-thumb,
main::-webkit-scrollbar-thumb,
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(150, 150, 150, 0.2);
  border-radius: 4px;
}
aside::-webkit-scrollbar-thumb:hover,
main::-webkit-scrollbar-thumb:hover,
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(150, 150, 150, 0.4);
}
</style>